import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, limit as firestoreLimit } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { useReview } from '../../context/ReviewContext';
import StoreReviewList from '../Review/StoreReviewList';
import StoreReviewForm from '../Review/StoreReviewForm';
import ProductCard from './ProductCard';
import LevelBadge from './LevelBadge';
import VerificationBadge, { RiskScoreBadge } from './VerificationBadge';

const StoreDetail = () => {
  const { t } = useTranslation();
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { canReviewStore } = useReview();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    fetchStore();
    fetchProducts();
  }, [storeId]);

  useEffect(() => {
    if (store?.id && currentUser?.uid) {
      checkCanReview();
    } else {
      setCanReview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id, currentUser?.uid]);

  const fetchStore = async () => {
    try {
      const storeRef = doc(db, 'stores', storeId);
      const storeSnap = await getDoc(storeRef);
      
      if (storeSnap.exists()) {
        setStore({ id: storeSnap.id, ...storeSnap.data() });
      }
    } catch (error) {
      console.error('Error fetching store:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef,
        where('storeId', '==', storeId),
        where('status', '==', 'active'),
        firestoreLimit(12)
      );
      
      const snapshot = await getDocs(q);
      const productsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const checkCanReview = async () => {
    if (!store || !currentUser) {
      setCanReview(false);
      return;
    }

    // Check if user has delivered orders from this store
    try {
      // This is a simplified check - in production, you'd check actual orders
      // For now, we'll allow any logged-in user to review
      setCanReview(true);
    } catch (error) {
      console.error('Error checking can review:', error);
      setCanReview(false);
    }
  };

  const handleReviewSuccess = () => {
    setShowReviewForm(false);
    fetchStore(); // Reload to show new review
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">{t('store.notFound')}</p>
          <button
            onClick={() => navigate('/marketplace')}
            className="mt-4 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            กลับไปหน้าหลัก
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8 pb-20 md:pb-8">
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/marketplace')}
          className="mb-4 text-green-600 hover:text-green-700 flex items-center gap-2 text-sm md:text-base"
        >
          ← กลับ
        </button>

        {/* Store Header - Mobile Optimized */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-4 md:mb-8">
          <div className="relative h-32 md:h-64 bg-gradient-to-br from-green-400 to-green-600">
            {store.coverImage ? (
              <img
                src={store.coverImage}
                alt={store.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white text-6xl">
                🏪
              </div>
            )}
            
            {/* Logo */}
            {store.logo && (
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
                <img
                  src={store.logo}
                  alt={store.name}
                  className="w-20 h-20 md:w-32 md:h-32 rounded-full border-2 md:border-4 border-white object-cover"
                />
              </div>
            )}
          </div>

          <div className="pt-12 md:pt-16 pb-4 md:pb-8 px-4 md:px-8 text-center">
            <h1 className="text-xl md:text-3xl font-bold text-gray-800 mb-2 md:mb-4">
              {store.name}
            </h1>
            
            {/* Level Badge */}
            {store.reputationLevel && (
              <div className="mb-4">
                <LevelBadge level={store.reputationLevel} score={store.reputationScore} type="store" />
              </div>
            )}
            
            {/* Verification Badge */}
            {store.verification && (
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                <VerificationBadge verification={store.verification} size="md" />
                {store.verification.riskScore !== undefined && (
                  <RiskScoreBadge riskScore={store.verification.riskScore} size="md" />
                )}
              </div>
            )}

            {/* Description */}
            {store.description && (
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                {store.description}
              </p>
            )}

            {/* Stats - Mobile Optimized */}
            <div className="flex items-center justify-center gap-3 md:gap-8 text-xs md:text-sm text-gray-600 flex-wrap">
              <div>
                <span className="font-semibold text-base md:text-lg">{store.completedOrders || 0}</span>
                <span className="ml-1">ออเดอร์</span>
              </div>
              <div>
                <span className="font-semibold text-base md:text-lg">{store.averageRating?.toFixed(1) || '0.0'}</span>
                <span className="ml-1 text-yellow-500">★</span>
              </div>
              {store.category && (
                <div>
                  <span className="px-2 md:px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs md:text-sm">
                    {store.category}
                  </span>
                </div>
              )}
            </div>

            {/* Contact Info - Mobile Optimized */}
            {store.contact && (
              <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 text-xs md:text-sm text-gray-600">
                  {store.contact.address && (
                    <div>
                      <span className="font-semibold">ที่อยู่:</span> {store.contact.address}
                    </div>
                  )}
                  {store.contact.phone && (
                    <div>
                      <span className="font-semibold">โทร:</span> {store.contact.phone}
                    </div>
                  )}
                  {store.contact.email && (
                    <div>
                      <span className="font-semibold">อีเมล:</span> {store.contact.email}
                    </div>
                  )}
                  {store.contact.lineId && (
                    <div>
                      <span className="font-semibold">LINE:</span> {store.contact.lineId}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Products Section - Mobile Optimized */}
        {products.length > 0 && (
          <div className="mb-4 md:mb-8">
            <h2 className="text-lg md:text-2xl font-bold text-gray-800 mb-3 md:mb-4">{t('store.products')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}

        {/* Reviews Section - Mobile Optimized */}
        <div className="bg-white rounded-lg shadow-lg p-4 md:p-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 md:mb-6 gap-3">
            <div className="flex items-center gap-2 md:gap-4 flex-wrap">
              <h2 className="text-lg md:text-2xl font-bold text-gray-800">{t('store.reviews')}</h2>
              <Link
                to={`/marketplace/reviews/store/${storeId}`}
                className="text-xs md:text-sm text-blue-600 hover:text-blue-800 underline"
              >
                {t('marketplace.viewAllReviews')}
              </Link>
            </div>
            {canReview && currentUser && (
              <button
                onClick={() => setShowReviewForm(true)}
                className="px-3 md:px-4 py-1.5 md:py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm md:text-base"
              >
                + {t('marketplace.writeReview')}
              </button>
            )}
          </div>

          {showReviewForm && (
            <div className="mb-6">
              <StoreReviewForm
                store={store}
                orderId={orderId}
                onClose={() => setShowReviewForm(false)}
                onSuccess={handleReviewSuccess}
              />
            </div>
          )}

          <StoreReviewList storeId={storeId} showStats={true} showResponse={true} />
        </div>
      </div>
    </div>
  );
};

export default StoreDetail;


